import React, { useEffect, useState, useCallback } from 'react';
import {
  ShieldCheck, ShieldAlert, Search, Clock, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronUp, ExternalLink,
} from 'lucide-react';
import { Card, Button, Input, Label, Badge, EmptyState } from '../components/ui';
import { useFirm } from '../contexts/FirmContext';
import { useAuth } from '../contexts/AuthContext';
import { runConflictCheck, saveConflictCheck, listConflictChecks } from '../lib/db';
import { formatDate, classNames } from '../lib/formatters';
import { useRoute } from '../lib/router';
import { PlanGate } from '../contexts/PlanContext';
import toast from '../lib/toast';

/* ─── severity helpers ─────────────────────────────────────────────────── */
const SEV_TONE   = { high: 'red', medium: 'amber', low: 'blue' };
const SEV_LABEL  = { high: 'High', medium: 'Medium', low: 'Low' };
const TYPE_LABEL = {
  email:        'Email match',
  phone:        'Phone match',
  name:         'Name match',
  organization: 'Organisation match',
  matter_title: 'Case file title match',
};

/* ─── HitCard ──────────────────────────────────────────────────────────── */
function HitCard({ hit, navigate }) {
  const [open, setOpen] = useState(true);
  const contact = hit.contact;
  const matter  = hit.matter;

  return (
    <div className={classNames(
      'rounded-xl border p-4',
      hit.severity === 'high'   && 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30',
      hit.severity === 'medium' && 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30',
      hit.severity === 'low'    && 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30',
    )}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge tone={SEV_TONE[hit.severity]}>{SEV_LABEL[hit.severity]} severity</Badge>
          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">{TYPE_LABEL[hit.type]}</span>
        </div>
        <button onClick={() => setOpen((v) => !v)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {open && (
        <div className="mt-3 space-y-2">
          {/* Contact hit */}
          {contact && (
            <div>
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {contact.first_name} {contact.last_name}
                {contact.organization && (
                  <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">
                    — {contact.organization}
                  </span>
                )}
              </p>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 space-x-3">
                {contact.email && <span>{contact.email}</span>}
                {contact.phone && <span>{contact.phone}</span>}
              </div>
              {/* Related matters */}
              {contact.matters?.length > 0 && (
                <div className="mt-2 space-y-1">
                  <p className="text-xs font-medium text-slate-600 dark:text-slate-400">Related matters:</p>
                  {contact.matters.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => navigate(`/matters/${m.id}`)}
                      className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      <ExternalLink size={11} />
                      {m.matter_number} — {m.title}
                      <Badge tone={m.status === 'open' ? 'blue' : 'slate'} className="ml-1">{m.status}</Badge>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Matter title hit */}
          {matter && !contact && (
            <button
              onClick={() => navigate(`/matters/${matter.id}`)}
              className="flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
            >
              <ExternalLink size={13} />
              {matter.matter_number} — {matter.title}
              {matter.client && (
                <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">
                  Client: {matter.client.first_name} {matter.client.last_name}
                </span>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── HistoryRow ───────────────────────────────────────────────────────── */
function HistoryRow({ check, onExpand, expanded }) {
  return (
    <div className="py-3 px-4 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition">
      {check.result === 'clear' ? (
        <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0" />
      ) : (
        <ShieldAlert size={18} className="text-red-500 flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
          {[check.query_name, check.query_email, check.query_org].filter(Boolean).join(' · ') || 'Unnamed check'}
        </p>
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
          {formatDate(check.created_at)} · {check.hit_count} hit{check.hit_count !== 1 ? 's' : ''}
        </p>
      </div>
      <Badge tone={check.result === 'clear' ? 'green' : 'red'}>
        {check.result === 'clear' ? 'Clear' : 'Conflict'}
      </Badge>
      <button
        onClick={() => onExpand(check.id)}
        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 ml-1"
      >
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
    </div>
  );
}

/* ─── Main page ────────────────────────────────────────────────────────── */
function ConflictCheckPageInner() {
  const { firmId }     = useFirm();
  const { userProfile } = useAuth();
  const [, navigate]   = useRoute();

  // Form
  const [form, setForm]   = useState({ name: '', email: '', phone: '', organization: '' });
  const f = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  // Run state
  const [running, setRunning]   = useState(false);
  const [hits, setHits]         = useState(null);   // null = not run yet
  const [notes, setNotes]       = useState('');
  const [saving, setSaving]     = useState(false);

  // History
  const [history, setHistory]       = useState([]);
  const [histLoading, setHistLoading] = useState(true);
  const [expandedId, setExpandedId]  = useState(null);

  useEffect(() => {
    if (!firmId) { setHistLoading(false); return; }
    listConflictChecks(firmId).then(({ data }) => {
      setHistory(data || []);
      setHistLoading(false);
    });
  }, [firmId]);

  const runCheck = useCallback(async (e) => {
    e?.preventDefault();
    if (!form.name && !form.email && !form.phone && !form.organization) return;
    setRunning(true);
    setHits(null);
    setNotes('');
    const { data, error } = await runConflictCheck(firmId, form);
    setRunning(false);
    if (error) { toast.error(error.message); return; }
    setHits(data || []);
  }, [firmId, form]);

  const highCount   = hits?.filter((h) => h.severity === 'high').length   ?? 0;
  const mediumCount = hits?.filter((h) => h.severity === 'medium').length  ?? 0;
  const lowCount    = hits?.filter((h) => h.severity === 'low').length     ?? 0;

  const markClear = async () => {
    setSaving(true);
    const payload = {
      checked_by: userProfile?.id,
      query_name: form.name || null,
      query_email: form.email || null,
      query_phone: form.phone || null,
      query_org: form.organization || null,
      result: 'clear',
      hit_count: hits?.length ?? 0,
      hits_json: hits,
      notes: notes || null,
    };
    const { data } = await saveConflictCheck(firmId, payload);
    setSaving(false);
    if (data) setHistory((prev) => [data, ...prev]);
    setHits(null);
    setForm({ name: '', email: '', phone: '', organization: '' });
    setNotes('');
  };

  const markConflict = async () => {
    setSaving(true);
    const payload = {
      checked_by: userProfile?.id,
      query_name: form.name || null,
      query_email: form.email || null,
      query_phone: form.phone || null,
      query_org: form.organization || null,
      result: 'conflict',
      hit_count: hits?.length ?? 0,
      hits_json: hits,
      notes: notes || null,
    };
    const { data } = await saveConflictCheck(firmId, payload);
    setSaving(false);
    if (data) setHistory((prev) => [data, ...prev]);
    setHits(null);
    setForm({ name: '', email: '', phone: '', organization: '' });
    setNotes('');
  };

  const toggleExpand = (id) => setExpandedId((v) => (v === id ? null : id));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Conflict Check</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Search for potential conflicts of interest before taking on a new client or matter
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ── Left: search form + results ── */}
        <div className="lg:col-span-3 space-y-5">

          {/* Search form */}
          <Card className="p-5">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">Search details</h2>
            <form onSubmit={runCheck} className="space-y-4">
              <div>
                <Label>Full name</Label>
                <Input placeholder="e.g. Kwame Mensah" value={form.name} onChange={f('name')} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Email</Label>
                  <Input type="email" placeholder="e.g. kwame@example.com" value={form.email} onChange={f('email')} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input type="tel" inputMode="tel" autoComplete="tel" placeholder="e.g. 0244000000" value={form.phone} onChange={f('phone')} />
                </div>
              </div>
              <div>
                <Label>Organisation / Company</Label>
                <Input placeholder="e.g. Mensah Enterprises Ltd" value={form.organization} onChange={f('organization')} />
              </div>
              <div className="flex justify-end">
                <Button type="submit" icon={Search} disabled={running || (!form.name && !form.email && !form.phone && !form.organization)}>
                  {running ? 'Searching…' : 'Run conflict check'}
                </Button>
              </div>
            </form>
          </Card>

          {/* Results */}
          {hits !== null && (
            <div className="space-y-4">
              {/* Summary banner */}
              <div className={classNames(
                'rounded-xl px-5 py-4 flex items-center gap-4',
                hits.length === 0
                  ? 'bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800'
                  : highCount > 0
                    ? 'bg-red-50 border border-red-200 dark:bg-red-950/30 dark:border-red-800'
                    : 'bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-800',
              )}>
                {hits.length === 0 ? (
                  <ShieldCheck size={32} className="text-emerald-500 flex-shrink-0" />
                ) : (
                  <ShieldAlert size={32} className={classNames('flex-shrink-0', highCount > 0 ? 'text-red-500' : 'text-amber-500')} />
                )}
                <div>
                  <p className={classNames(
                    'text-base font-semibold',
                    hits.length === 0 ? 'text-emerald-800 dark:text-emerald-200'
                      : highCount > 0 ? 'text-red-800 dark:text-red-200'
                      : 'text-amber-800 dark:text-amber-200',
                  )}>
                    {hits.length === 0
                      ? 'No conflicts found'
                      : `${hits.length} potential conflict${hits.length > 1 ? 's' : ''} found`}
                  </p>
                  <p className={classNames(
                    'text-xs mt-0.5',
                    hits.length === 0 ? 'text-emerald-600 dark:text-emerald-400'
                      : highCount > 0 ? 'text-red-600 dark:text-red-400'
                      : 'text-amber-600 dark:text-amber-400',
                  )}>
                    {hits.length === 0
                      ? 'This person does not appear in any existing contacts or matters.'
                      : `${highCount} high · ${mediumCount} medium · ${lowCount} low severity`}
                  </p>
                </div>
              </div>

              {/* Hit cards */}
              {hits.length > 0 && (
                <div className="space-y-3">
                  {hits.map((hit, i) => (
                    <HitCard key={i} hit={hit} navigate={navigate} />
                  ))}
                </div>
              )}

              {/* Notes + Save */}
              <Card className="p-5 space-y-4">
                <div>
                  <Label>Notes (optional)</Label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Add any notes about this conflict check…"
                    className="w-full mt-1 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  />
                </div>
                <div className="flex items-center gap-3 justify-end">
                  <Button
                    variant="secondary"
                    icon={AlertTriangle}
                    disabled={saving}
                    onClick={markConflict}
                    className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950/30"
                  >
                    {saving ? 'Saving…' : 'Flag as conflict'}
                  </Button>
                  <Button
                    icon={CheckCircle2}
                    disabled={saving}
                    onClick={markClear}
                    className="bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500"
                  >
                    {saving ? 'Saving…' : 'Clear — no conflict'}
                  </Button>
                </div>
              </Card>
            </div>
          )}
        </div>

        {/* ── Right: history ── */}
        <div className="lg:col-span-2">
          <Card>
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <Clock size={15} /> Recent checks
              </h2>
            </div>
            {histLoading ? (
              <p className="p-6 text-sm text-slate-400 text-center">Loading…</p>
            ) : history.length === 0 ? (
              <EmptyState
                icon={ShieldCheck}
                title="No checks yet"
                description="Run a conflict check to see history here."
              />
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {history.map((check) => (
                  <div key={check.id}>
                    <HistoryRow
                      check={check}
                      onExpand={toggleExpand}
                      expanded={expandedId === check.id}
                    />
                    {expandedId === check.id && check.notes && (
                      <div className="px-4 pb-3">
                        <p className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 rounded p-2">
                          {check.notes}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

export default function ConflictCheckPage() {
  return <PlanGate feature="conflict_check"><ConflictCheckPageInner /></PlanGate>;
}
